import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SectionCard } from '../components/ui/SectionCard';
import { AppButton } from '../components/ui/AppButton';
import { useAuth } from '../hooks/useAuth';
import { LOGIN_SWITCH_ACCOUNT_QUERY, ROUTES } from '../routes/paths';
import { getEmailValidationError } from '../utils/emailValidation';
import { useToast } from '../hooks/useToast';
import { BRAND_NAME } from '../config/branding';
import { AppVersionLabel } from '../components/ui/AppVersionLabel';

const LOGIN_HINT_KEY = 'lms-login-switch-hint';

export function LoginPage({ onLogin }) {
  const { logout } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const switchAccount = searchParams.get(LOGIN_SWITCH_ACCOUNT_QUERY) === '1';
  const [showSwitchHint, setShowSwitchHint] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!switchAccount) return;
    sessionStorage.setItem(LOGIN_HINT_KEY, '1');
    logout();
    navigate(ROUTES.login, { replace: true });
  }, [switchAccount, logout, navigate]);

  useEffect(() => {
    if (sessionStorage.getItem(LOGIN_HINT_KEY) === '1') {
      sessionStorage.removeItem(LOGIN_HINT_KEY);
      setShowSwitchHint(true);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const email = form.querySelector('input[type="email"]')?.value ?? '';
    const password = form.querySelector('input[name="password"]')?.value ?? '';
    const emailError = getEmailValidationError(email);
    if (emailError) {
      showToast(emailError, 'error');
      return;
    }
    if (!password) {
      showToast('Password is required', 'error');
      return;
    }
    void onLogin(email, password);
  };

  const inputClass =
    'w-full px-4 py-3 border border-slate-300 rounded-xl bg-white text-slate-900 placeholder:text-slate-400 transition-shadow duration-200 ' +
    'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-400 focus:shadow-sm';

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-8 sm:p-6 md:p-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <SectionCard
          as="div"
          className="shadow-xl border-slate-200/90 p-6 sm:p-8 md:p-10"
          padded={false}
        >
          <h2 className="text-xl sm:text-2xl md:text-3xl font-black text-slate-900 mb-1 sm:mb-2 tracking-tight">
            {BRAND_NAME}
          </h2>
          <p className="text-sm text-slate-500 font-medium mb-6 sm:mb-8">Sign in to continue</p>

          {showSwitchHint && (
            <p
              role="status"
              className="mb-4 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-medium text-indigo-900"
            >
              Any previous account on this browser was signed out. Copy the email and temporary
              password from the credentials page, then sign in below.
            </p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
            <div>
              <label htmlFor="login-email" className="sr-only">
                Email
              </label>
              <input
                id="login-email"
                type="email"
                placeholder="Email"
                required
                autoComplete="off"
                className={inputClass}
              />
            </div>
            <div className="relative">
              <label htmlFor="login-password" className="sr-only">
                Password
              </label>
              <input
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                required
                autoComplete="off"
                className={`${inputClass} pr-12`}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-xl text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-5 h-5"
                    aria-hidden
                  >
                    <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.8 21.8 0 0 1 5.06-6.94" />
                    <path d="M1 1l22 22" />
                    <path d="M9.9 9.9a3 3 0 0 0 4.24 4.24" />
                    <path d="M14.12 14.12 9.88 9.88" />
                    <path d="M12 4c7 0 11 8 11 8a21.8 21.8 0 0 1-4.87 6.13" />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-5 h-5"
                    aria-hidden
                  >
                    <path d="M2.46 12s3.64-7 9.54-7 9.54 7 9.54 7-3.64 7-9.54 7-9.54-7-9.54-7z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            <AppButton type="submit" variant="primary" size="lg" className="w-full mt-2">
              Login
            </AppButton>
            <div className="flex items-center justify-between pt-2">
              <Link
                to="/forgot-password"
                className="text-sm font-bold text-indigo-600 hover:text-indigo-800 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 rounded-lg px-1 -mx-1"
              >
                Forgot password?
              </Link>
            </div>
          </form>
          <div className="mt-6 border-t border-slate-100 pt-4">
            <AppVersionLabel align="center" />
          </div>
        </SectionCard>
      </motion.div>
    </div>
  );
}
